import {useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import {InputEstimateTreeOperationDto} from '../../../shared/dtos/estimate-tree-operation.dto';

const treeWork = ['Tree Removal', 'Thinning', 'Crown Reduction', 'Ground to Canopy Clearance'];
const pruning = ['Tree Removal', 'Thinning', 'Crown Reduction'];
const impact = ['Little or none', 'Minor', 'Moderate', 'Substantial', 'Severe'];
const features = [
  {key: 'house_impact', label: 'House impact', operations: pruning, options: impact},
  {key: 'fence_impact', label: 'Fence impact', operations: pruning, options: impact},
  {key: 'landscaping_impact', label: 'Landscaping impact', operations: pruning, options: impact},
  {key: 'power_line_impact', label: 'Power-line impact', operations: pruning, options: impact},
  {key: 'canal_water_impact', label: 'Canal or water impact', operations: pruning, options: impact},
  {key: 'general_obstacle_impact', label: 'Obstacle impact', operations: ['Ground to Canopy Clearance'], options: impact},
  {key: 'vegetation_under_tree', label: 'Vegetation under tree', operations: treeWork, options: ['Little or none', 'Light', 'Moderate', 'Heavy', 'Very heavy']},
  {key: 'overgrown_grass', label: 'Overgrown grass', operations: treeWork, options: ['Little or none', 'Light', 'Moderate', 'Heavy', 'Very heavy']},
  {key: 'foliage_density', label: 'Foliage density', operations: ['Thinning', 'Ground to Canopy Clearance'], options: ['Very sparse', 'Light', 'Moderate', 'Dense', 'Very dense']},
  {key: 'ground_type', label: 'Ground type', operations: ['Tree Removal'], options: []},
  {key: 'curbing', label: 'Curbing under tree', operations: ['Tree Removal'], options: ['Yes', 'No']},
  {key: 'roadway_overhang', label: 'Roadway overhang', operations: ['Tree Removal'], options: ['Yes', 'No']},
];

/** The work item's photos, pinned above the questions they answer, with a large viewer. */
function PhotoStrip({photos}: {photos: {id: number; url: string}[]}) {
  const [viewing, setViewing] = useState<number | null>(null);
  if (!photos.length) return null;
  const step = (by: number) => setViewing(index => index === null ? null : (index + by + photos.length) % photos.length);
  return <>
    <Stack direction="row" alignItems="center" spacing={1} data-testid="photo-strip"
           sx={{position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', py: 1, borderBottom: 1, borderColor: 'divider',
             // The scroll area's own padding would show content above the strip; cover it.
             boxShadow: theme => `0 -16px 0 ${theme.palette.background.paper}`}}>
      <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>Photos</Typography>
      <Stack direction="row" spacing={1} sx={{overflowX: 'auto'}}>
        {photos.map((photo, index) => (
          <Box key={photo.id} component="button" type="button" aria-label={`Enlarge photo ${index + 1} of ${photos.length}`}
               onClick={() => setViewing(index)}
               sx={{p: 0, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', flex: '0 0 auto', cursor: 'zoom-in', bgcolor: 'transparent'}}>
            <Box component="img" src={photo.url} alt={`Photo ${index + 1}`} sx={{display: 'block', height: 56, maxWidth: 96, objectFit: 'cover'}}/>
          </Box>
        ))}
      </Stack>
    </Stack>
    <Dialog open={viewing !== null} onClose={() => setViewing(null)} maxWidth="lg" aria-label="Photo viewer">
      <DialogContent sx={{p: 1}}>
        {viewing !== null && <Box component="img" src={photos[viewing].url} alt={`Photo ${viewing + 1} of ${photos.length}`}
                                  sx={{display: 'block', maxWidth: '100%', maxHeight: '75vh', mx: 'auto'}}/>}
      </DialogContent>
      <DialogActions>
        {photos.length > 1 && <Button onClick={() => step(-1)}>Previous photo</Button>}
        {photos.length > 1 && <Button onClick={() => step(1)}>Next photo</Button>}
        <Button onClick={() => setViewing(null)}>Close</Button>
      </DialogActions>
    </Dialog>
  </>;
}

export function PhotoContextFields({operation, inferred, onChange, disabled, photos}: {
  operation: InputEstimateTreeOperationDto; inferred: Record<string, string | number>;
  onChange: (key: string, value: string | number | undefined) => void; disabled: boolean;
  photos: {id: number; url: string}[];
}) {
  const applicable = features.filter(f => f.operations.includes(operation.operationType));
  if (!applicable.length) return null;
  return <Accordion defaultExpanded disableGutters elevation={0}>
    <AccordionSummary expandIcon={<ExpandMore/>}><Typography variant="subtitle2">Site conditions · photo context</Typography></AccordionSummary>
    <AccordionDetails><Stack spacing={2}>
      <PhotoStrip photos={photos}/>
      {applicable.map(f => {
        const key = `photoContext_${f.key}`;
        const value = operation.details[key];
        const hint = inferred[f.key];
        const label = (v: string | number) => typeof v === 'number' ? f.options[v - 1] ?? String(v) : v;
        return <Stack key={key} spacing={0.5}>
          <Typography variant="subtitle2" id={key}>{f.label}</Typography>
          {f.options.length ? <ToggleButtonGroup exclusive size="small" aria-labelledby={key} disabled={disabled}
            value={value ?? null} onChange={(_, v) => onChange(key, v ?? undefined)} sx={{flexWrap: 'wrap'}}>
            {f.options.map((option, index) => <ToggleButton key={option} value={f.options.length === 2 ? option : index + 1}
              sx={{textTransform: 'none'}}>{option}</ToggleButton>)}
          </ToggleButtonGroup> : <TextField size="small" label={f.label} value={value ?? ''} disabled={disabled}
            placeholder="Unknown" inputProps={{maxLength: 100}} onChange={e => onChange(key, e.target.value || undefined)}/>}
          {value === undefined && hint !== undefined && <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">AI suggests: {label(hint)}</Typography>
            <Button size="small" disabled={disabled} onClick={() => onChange(key, hint)}>Use suggestion</Button>
          </Stack>}
          {value !== undefined && <Button size="small" disabled={disabled} sx={{alignSelf: 'flex-start'}} onClick={() => onChange(key, undefined)}>Clear observation</Button>}
        </Stack>;
      })}
    </Stack></AccordionDetails>
  </Accordion>;
}
